import { useLang } from 'hooks/lang';

import { actions, FullTask, isOwner, taskStatusByCode, taskStatusesConfig } from 'entities';

import { Button } from 'components/Blocks';

import { Deadline } from 'pages/Projects/components/Deadline';

import { useTaskModal } from '../../context.taskModal';
import { useSingleJustification } from 'providers/Justification';
import { useToast } from 'providers/Toast';
import { useUser } from 'hooks';

import { formatDate, getLocalDateString } from 'utils/date';

interface SubTaskDateProps {
  subTask: FullTask['subTasks'][number];
  shouldDisableDate: (date: Date | null) => boolean;
}

export function SubTaskDate({ subTask, shouldDisableDate }: SubTaskDateProps) {
  const { t } = useLang();
  const { addToast } = useToast();
  const loggedUser = useUser();

  const { task, updateTask } = useTaskModal();

  const canChange = [
    isOwner(task, loggedUser) || isOwner(subTask, loggedUser),
    taskStatusByCode[task.status].canChangeDate,
    taskStatusesConfig.done.code !== subTask.status,
  ].every(Boolean);

  const changeDate = actions.projects.changeSubTaskDate.use({
    onSuccess: (_, { date }) => {
      updateTask((old) => ({
        ...old,
        subTasks: old.subTasks.map((sub) =>
          sub.id !== subTask.id ? sub : { ...sub, expectedDate: date },
        ),
      }));
    },
  });

  const openChangeRequest = useSingleJustification({
    shouldDisableDate,

    type: 'date',

    cancel: () => addToast({ title: t('expected-date-change-aborted') }),

    text: t('sub-change-date-text'),

    onConfirm: async (justification, newDate) => {
      await changeDate.execute({
        project: task.projectId,
        task: task.id,
        subTask: subTask.id,
        justification,
        date: getLocalDateString(newDate!),
      });
    },
  });

  return (
    <Button
      variant="ghost"
      onClick={canChange ? openChangeRequest : undefined}
      className="w-full py-0.5 font-normal text-inherit disabled:opacity-100"
      disabled={!canChange}
      tooltipContent={t('change-date')}
    >
      <Deadline
        text={subTask.expectedDate ? formatDate(subTask.expectedDate) : '-'}
        target={subTask}
        className="h-2 w-[70%] max-w-[180px]"
      />
    </Button>
  );
}
